import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import safeLocalStorage from 'client/lib/safeLocalStorage';

import {
  MEDIA_QUERY_SMALL_SCREEN,
} from 'client/constants/mediaQueries';

import {
  STRIPE_OBJECT_TYPE_CARD,
  STRIPE_OBJECT_TYPE_BANK_ACCOUNT,
  STRIPE_CARD_DECLINED_ERROR_MAPPING,
} from 'common/stripeConstants';

import {
  SR_API_ACTION_USER_CREATE_PAYMENT_ACCOUNT,
} from 'api/actionTypes';

import {
  stripeToPaymentAccountMapping,
  PAYMENT_ACCOUNT_BANK_ACCOUNT,
} from 'common/paymentAccountConstants';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import {
  SR_CHECKBOX_LIST_THEME_ICON_BOX,
} from 'client/components/Checkbox';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrModalV2, {
  MODAL_THEME_FIXED_FOOTER_BUTTON,
  MODAL_THEME_FIXED_FOOTER_BUTTON_SLIDE_UP,
} from 'client/components/SrModalV2/SrModalV2';
import OpenPlaidLink from 'client/components/OpenPlaidLink';
import SrStripeCardFormModal from 'client/components/SrStripeCardFormModal';

const stripeSourceTypeFormField = {
  id: 'leaseTemplateType',
  type: 'multiple_choice',
  properties: {
    choices: [
      {
        label: 'Bank Account',
        sublabel: '1% fee',
        value: STRIPE_OBJECT_TYPE_BANK_ACCOUNT,
        iconUrl: '/images/bank-accounts-grey.svg',
        activeIconUrl: '/images/bank-accounts-green.svg',
        noBackgroundImage: true,
      },
      {
        label: 'Card',
        sublabel: '3% fee',
        value: STRIPE_OBJECT_TYPE_CARD,
        iconUrl: '/images/credit-cards-grey.svg',
        activeIconUrl: '/images/credit-cards-green.svg',
        noBackgroundImage: true,
      },
    ],
    multipleSelection: false,
    fieldType: SR_CHECKBOX_LIST_THEME_ICON_BOX,
  },
};

const SrChoosePaymentAccountModal = ({
  stripeFee,
  userCreatePaymentAccountInProgress,
  changePaymentAccountType,
  partner,
  paymentAccounts,
  plaidLinkToken,
  plaidLinkJSON,
  onPlaidLinkSuccess,
  onPlaidLinkExit,
  onPaymentAccount,
  onCancel,
}) => {
  let initialStripeSourceType = null;

  if (!partner.experienceConfiguration.achEnabled) {
    initialStripeSourceType = STRIPE_OBJECT_TYPE_CARD;
  }

  const [stripeSourceType, setStripeSourceType] = useState(initialStripeSourceType);
  const [errorMessage, setErrorMessage] = useState(null);

  const dispatch = useDispatch();

  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const setNullStripeSourceType = () => {
    if (!partner.experienceConfiguration.achEnabled) {
      onCancel();
      return;
    }

    setStripeSourceType(null);
  };

  const _onPlaidLinkExit = (error, metadata) => {
    setNullStripeSourceType();
    onPlaidLinkExit(error, metadata);
  };

  const onSelectStripeSourceType = (_stripeSourceType) => {
    const paymentAccountType = stripeToPaymentAccountMapping[_stripeSourceType];
    const paymentAccount = paymentAccounts
      .find((paymentAccount) => paymentAccount.type === paymentAccountType);

    if (paymentAccount && (changePaymentAccountType !== paymentAccount.type)) {
      onPaymentAccount(paymentAccount);
    }
    else {
      if (paymentAccountType === PAYMENT_ACCOUNT_BANK_ACCOUNT && plaidLinkJSON) {
        safeLocalStorage.setItem('plaidLinkJSON', JSON.stringify(plaidLinkJSON));
      }

      setStripeSourceType(_stripeSourceType);
    }
  };

  const onStripeSource = (source) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_USER_CREATE_PAYMENT_ACCOUNT,
      payload: {
        stripeData: {
          token: source.id,
        },
      },
      onSuccess: onPaymentAccount,
      onError: (error) => {
        const message = STRIPE_CARD_DECLINED_ERROR_MAPPING[error.decline_code]
          || error.message;

        setErrorMessage(message);
      },
    });
  };

  if (stripeSourceType === STRIPE_OBJECT_TYPE_CARD) {
    return (
      <SrStripeCardFormModal
        stripeFee={stripeFee}
        inProgress={userCreatePaymentAccountInProgress}
        errorMessage={errorMessage}
        onCancel={setNullStripeSourceType}
        onSource={onStripeSource}
      />
    );
  }

  return (
    <SrModalV2
      theme={isSmallScreen
        ? MODAL_THEME_FIXED_FOOTER_BUTTON_SLIDE_UP
        : MODAL_THEME_FIXED_FOOTER_BUTTON
      }
      contentMaxWidth={!isSmallScreen ? '450px' : null}
      buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
      buttonText='Cancel'
      onClose={onCancel}
    >
      <SrBox margin='4px 15px 14px 15px'>
        <SrFlex justify='center'>
          <SrText
            color='blueGreycliff'
            font='greycliff'
            fontSize='30px'
            lineHeight='36px'
            weight='800'
            align='center'
            block='block'
          >
            Choose Payment Type
          </SrText>
        </SrFlex>
        <SrFlex>
          <SrFormikField
            formField={{
              ...stripeSourceTypeFormField,
              properties: {
                ...stripeSourceTypeFormField.properties,
                onChange: onSelectStripeSourceType,
              },
            }}
            theme={SR_FIELD_THEME_ADOBE}
          />
        </SrFlex>
      </SrBox>
      {stripeSourceType === STRIPE_OBJECT_TYPE_BANK_ACCOUNT && (
        <OpenPlaidLink
          linkToken={plaidLinkToken}
          onExit={_onPlaidLinkExit}
          onSuccess={onPlaidLinkSuccess}
        />
      )}
    </SrModalV2>
  );
};

SrChoosePaymentAccountModal.propTypes = {
  stripeFee: PropTypes.number.isRequired,
  userCreatePaymentAccountInProgress: PropTypes.bool,
  changePaymentAccountType: PropTypes.string,
  partner: PropTypes.object.isRequired,
  paymentAccounts: PropTypes.array.isRequired,
  plaidLinkToken: PropTypes.string,
  plaidLinkJSON: PropTypes.object,
  onPlaidLinkSuccess: PropTypes.func,
  onPaymentAccount: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SrChoosePaymentAccountModal.displayName = 'SrChoosePaymentAccountModal';

export default SrChoosePaymentAccountModal;
